import { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import LoaderComp from "../../../components/LoaderComp";
import PaginationComp from "../../../components/PaginationComp";
import RenderToast from "../../../components/RenderToast";
import useDebounce from "../../../hooks/useDebounce";
import EditCategoryModal from "../../../Modal/Category/EditCategoryModal";
import RemoveCategoryModal from "../../../Modal/Category/RemoveCategoryModal";
import ViewCategoryModal from "../../../Modal/Category/ViewCategoryModal";
import API from "../../../utils/api";
import classes from "./ViewCategory.module.css";

const PER_PAGE = 10;

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function ViewCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [currentPage, setCurrentPage] = useState(1);

  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [user, setUser] = useState(readUser());

  const [bulkDeleteTargets, setBulkDeleteTargets] = useState([]);

  useEffect(() => {
    const handleStorage = () => setUser(readUser());
    const handleUserUpdate = () => setUser(readUser());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("userUpdated", handleUserUpdate);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  const can = (permission) => user && Number(user[permission]) === 1;
  const canViewCategory = can("category_view");
  const canAddCategoryButton = can("category_view_add_button");
  const canDeleteSelectedButton = can("category_view_delete_selected_button");
  const canEditCategory = can("category_view_edit_action");
  const canViewCategoryAction = can("category_view_view_action");
  const canDeleteCategory = can("category_view_delete_action");

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await API.post("/categories.php", { action: "fetch" });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const normalized = data.map((cat) => ({
        ...cat,
        count: Number(cat.count) || 0,
        is_uncategorized: Number(cat.is_uncategorized) === 1,
      }));
      setCategories(normalized);
    } catch (err) {
      console.error(err);
      RenderToast({ type: "error", message: "Failed to load categories" });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setSelected(new Set());
  }, [categories]);

  const filteredCategories = useMemo(() => {
    if (!debouncedSearch?.trim()) {
      return categories;
    }
    const term = debouncedSearch.toLowerCase();
    return categories.filter(
      (cat) =>
        cat.categoryName.toLowerCase().includes(term) ||
        cat.category_slug.toLowerCase().includes(term) ||
        (cat.parentName || "").toLowerCase().includes(term),
    );
  }, [categories, debouncedSearch]);

  const total = filteredCategories.length;
  const totalPages = Math.ceil(total / PER_PAGE);
  const paginated = filteredCategories.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  const selectableOnPage = paginated.filter((cat) => !cat.is_uncategorized);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleEdit = (category) => {
    if (!canEditCategory) return;
    setEditTarget(category);
  };

  const handleDelete = (category) => {
    if (!canDeleteCategory || category?.is_uncategorized) return;
    setDeleteTarget(category);
  };
  const handleView = (category) => {
    if (!canViewCategoryAction) return;
    setViewTarget(category);
  };

  const closeEditModal = () => {
    setEditTarget(null);
    fetchCategories();
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    fetchCategories();
  };

  const toggleSelect = (id) => {
    if (!canDeleteSelectedButton) return;
    const target = categories.find((cat) => cat.id === id);
    if (target?.is_uncategorized) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!canDeleteSelectedButton) return;
    const selectableIds = paginated
      .filter((cat) => !cat.is_uncategorized)
      .map((cat) => cat.id);
    const allSelected =
      selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

    if (allSelected) {
      const next = new Set(selected);
      selectableIds.forEach((id) => next.delete(id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      selectableIds.forEach((id) => next.add(id));
      setSelected(next);
    }
  };

  // const handleBulkDelete = async () => {
  //   if (!canDeleteSelectedButton) return;
  //   if (selected.size === 0) return;
  //   if (
  //     !window.confirm(
  //       `Delete ${selected.size} selected categor${
  //         selected.size === 1 ? "y" : "ies"
  //       }?`
  //     )
  //   ) {
  //     return;
  //   }
  //   const idsToDelete = Array.from(selected).filter((id) => {
  //     const cat = categories.find((c) => c.id === id);
  //     return cat && !cat.is_uncategorized;
  //   });
  //   if (idsToDelete.length === 0) {
  //     RenderToast({
  //       type: "info",
  //       message: "Default category cannot be deleted.",
  //     });
  //     return;
  //   }
  //   try {
  //     setBulkDeleting(true);
  //     const token = localStorage.getItem("authToken") || "";
  //     const params = new URLSearchParams();
  //     params.append("action", "bulk_delete");
  //     params.append("ids", JSON.stringify(idsToDelete));
  //     const response = await fetch(`${API.base}/categories.php`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: token,
  //         "Content-Type": "application/x-www-form-urlencoded",
  //       },
  //       body: params,
  //     });
  //     const data = await response.json();
  //     if (!response.ok || !data.success) {
  //       throw new Error(data.error || "Failed to delete categories");
  //     }
  //     RenderToast({ type: "success", message: "Categories deleted" });
  //     setSelected(new Set());
  //     fetchCategories();
  //   } catch (err) {
  //     RenderToast({ type: "error", message: err.message });
  //   } finally {
  //     setBulkDeleting(false);
  //   }
  // };

  // Remove window.confirm and show modal for bulk delete
  const handleBulkDelete = () => {
    if (!canDeleteSelectedButton) return;
    if (selected.size === 0) return;

    const idsToDelete = Array.from(selected)
      .map((id) => categories.find((c) => c.id === id))
      .filter((cat) => cat && !cat.is_uncategorized);

    if (idsToDelete.length === 0) {
      RenderToast({
        type: "info",
        message: "Default category cannot be deleted.",
      });
      return;
    }

    setBulkDeleteTargets(idsToDelete); // open modal
  };

  if (!canViewCategory) {
    return (
      <DashboardLayout>
        <div className={classes.page}>
          <div className={classes.emptyRow}>
            You do not have permission to view categories.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={classes.page}>
        <div className={classes.rowTop}>
          <h3 className={classes.pageTitle}>View Categories</h3>
          <div className={classes.controlsWrap}>
            {canAddCategoryButton && (
              <button
                className={classes.btnPrimary}
                onClick={() => navigate("/category/add")}
              >
                <FiPlus /> Add Category
              </button>
            )}
            {canDeleteSelectedButton && (
              <button
                className={classes.btnDeleteSelected}
                onClick={handleBulkDelete}
                disabled={selected.size === 0 || bulkDeleting}
              >
                {bulkDeleting
                  ? "Deleting..."
                  : `Delete Selected (${selected.size})`}
              </button>
            )}
            <div className={classes.searchWrap}>
              <FiSearch className={classes.searchIcon} />
              <input
                type="search"
                className={classes.searchInput}
                placeholder="Search by name, slug, or parent..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={classes.rowBottom}>
          <div className={classes.tableCard}>
            {loading ? (
              <LoaderComp />
            ) : (
              <>
                <div className={classes.tableWrapper}>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        {canDeleteSelectedButton && (
                          <th>
                            <input
                              type="checkbox"
                              disabled={selectableOnPage.length === 0}
                              checked={
                                selectableOnPage.length > 0 &&
                                selectableOnPage.every((cat) =>
                                  selected.has(cat.id),
                                )
                              }
                              onChange={toggleSelectAll}
                            />
                          </th>
                        )}
                        <th>S.No</th>
                        <th>Category</th>
                        <th>Parent</th>
                        <th>Slug</th>
                        <th>Description</th>
                        <th>Products</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td
                            colSpan={canDeleteSelectedButton ? 8 : 7}
                            className={classes.emptyRow}
                          >
                            No categories found
                          </td>
                        </tr>
                      ) : (
                        paginated.map((cat, index) => (
                          <tr key={cat.id}>
                            {canDeleteSelectedButton && (
                              <td>
                                <input
                                  type="checkbox"
                                  disabled={cat.is_uncategorized}
                                  checked={selected.has(cat.id)}
                                  onChange={() => toggleSelect(cat.id)}
                                />
                              </td>
                            )}
                            <td>{(currentPage - 1) * PER_PAGE + index + 1}</td>
                            <td>
                              <div className={classes.nameStack}>
                                <span className={classes.categoryName}>
                                  {cat.categoryName}
                                </span>
                                {cat.is_uncategorized && (
                                  <span className={classes.defaultBadge}>
                                    Default
                                  </span>
                                )}
                                {cat.parentName && (
                                  <span className={classes.subText}>
                                    Child of {cat.parentName}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{cat.parentName || "—"}</td>
                            <td>
                              <code className={classes.slug}>
                                {cat.category_slug}
                              </code>
                            </td>
                            <td className={classes.descriptionColumn}>
                              {cat.description || "—"}
                            </td>
                            <td>
                              <span className={classes.countPill}>
                                {cat.count} item{cat.count === 1 ? "" : "s"}
                              </span>
                            </td>
                            <td>
                              <div className={classes.actionGroup}>
                                {canViewCategoryAction && (
                                  <button
                                    className={classes.iconBtn}
                                    title="View"
                                    onClick={() => handleView(cat)}
                                  >
                                    <FiEye />
                                  </button>
                                )}
                                {canEditCategory && (
                                  <button
                                    className={classes.iconBtnGreen}
                                    title="Edit"
                                    onClick={() => handleEdit(cat)}
                                  >
                                    <FiEdit2 />
                                  </button>
                                )}
                                {canDeleteCategory && !cat.is_uncategorized && (
                                  <button
                                    className={classes.iconBtnRed}
                                    title="Delete"
                                    onClick={() => handleDelete(cat)}
                                  >
                                    <FiTrash2 />
                                  </button>
                                )}
                                {!canViewCategoryAction &&
                                  !canEditCategory &&
                                  !canDeleteCategory && (
                                    <span className={classes.noActions}>—</span>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className={classes.paginationWrap}>
                  <div className={classes.totalCount}>
                    Total Categories: <strong>{total}</strong>
                  </div>
                  <PaginationComp
                    total={total}
                    perPage={PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {editTarget && (
          <EditCategoryModal category={editTarget} onClose={closeEditModal} />
        )}
        {/* {deleteTarget && (
          <RemoveCategoryModal
            category={deleteTarget}
            onClose={closeDeleteModal}
          />
        )} */}
        {viewTarget && canViewCategoryAction && (
          <ViewCategoryModal
            category={viewTarget}
            onClose={() => setViewTarget(null)}
          />
        )}

        {/* Single delete */}
        {deleteTarget && (
          <RemoveCategoryModal
            category={deleteTarget}
            onClose={closeDeleteModal}
          />
        )}

        {/* Bulk delete */}
        {bulkDeleteTargets.length > 0 && (
          <RemoveCategoryModal
            categories={bulkDeleteTargets} // pass multiple categories
            onClose={() => {
              setBulkDeleteTargets([]);
              fetchCategories();
              setSelected(new Set());
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
